import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { React, useEffect, useState } from "react";
import { useMount, useUnmount } from "react-use";
import Card from "shared/Card";
import { useQueryParams } from "shared/@hooks/useQueryParams";
import { Column, LabelGroup, Row, Section } from "shared/layout";
import Link from "shared/Link";
import PinLabel from "shared/PinLabel";
import { descendingBy } from "shared/@utils/sort";
import { RECENT_IBOS_KEY } from "../index";
import Tabs from "./Tabs";
import styles from "./index.module.css";
import LOS from "./LOS";
import Membership from "./Membership";
import UsernameEdit from "./UsernameEdit";
import NonMember from "./NonMember";

// import SuspensionBanner from "Security/Shared/SuspensionBanner";
// import { useIBOFile } from "Security/IBOFilePage/useIBOFile";

dayjs.extend(utc);
dayjs.extend(timezone);

function getMemberUrl(iboNumber) {
  return `/membership/lookup/ibo/${iboNumber}`;
}

function appendToRecents(iboNumber, name) {
  const recents = JSON.parse(sessionStorage.getItem(RECENT_IBOS_KEY) || "[]");

  // Keep the latest 9 results.
  let newList = recents.filter((item) => item.iboNumber !== iboNumber);
  newList.sort(descendingBy("timestamp"));
  newList = newList.slice(0, 9);

  // Prepend this one.
  newList.unshift({
    iboNumber,
    name,
    timestamp: new Date(),
  });

  sessionStorage.setItem(RECENT_IBOS_KEY, JSON.stringify(newList));
}

const pinMap = {
  diamond: 4,
  emerald: 3,
  platinum: 2,
  "associate ibo": 1,
};

export default function Details({ iboInfo, iboNumber }) {
  const {
    primaryInfo,
    membership,
    lineOfSponsorship: los,
    hasFreeTrial,
    hasDigitalDelivery,
    hasDreamStream,
    hasPendingDeliveryDowngrade,
    hasPendingDreamStreamDowngrade,
    hasPendingMembershipTransactions,
  } = iboInfo;
  const { iboDisplayName, iboName, iboA } = primaryInfo;

  // Warnings from Security app
  //const { data: platformStatus } = useIBOFile(iboNumber);

  useMount(() => {
    appendToRecents(iboNumber, iboDisplayName);
  });

  useUnmount(() => {
    appendToRecents(iboNumber, iboDisplayName);
  });

  const query = useQueryParams();
  const pinLevel = pinMap[los.pinLevel.toLowerCase()];

  const [platforms, setPlatforms] = useState(null);
  const [isSuspended, setIsSuspended] = useState(false);

  // Warnings from Security app
  // useEffect(() => {
  //   if (platformStatus) {
  //     setPlatforms(platformStatus);

  //     const isSuspended = platformStatus.some(
  //       (platform) => platform.status === "suspended"
  //     );

  //     setIsSuspended(isSuspended);
  //   }
  // }, [platformStatus]);

  return (
    <div className={styles.container}>
      {membership == null ? (
        <>
          <header>
            <h1>IBO {iboNumber}</h1>
          </header>

          <NonMember iboNumber={iboNumber} />
        </>
      ) : (
        <>
          <header>
            <span className={styles.headerIBONumber}>IBO {iboNumber}</span>
            <h1>{iboName}</h1>
          </header>
          {/* {isSuspended && <SuspensionBanner suspensionInfo={platforms} />} */}
          <Card>
            <Section>
              <header>
                <h3>Primary Info</h3>
              </header>
              <Row>
                <Column width="30%">
                  <LabelGroup title="IBO Name">{iboDisplayName}</LabelGroup>
                </Column>
                <Column width="23.3333%">
                  <LabelGroup title="IBO ID">{iboNumber}</LabelGroup>
                </Column>
                <Column width="23.3333%">
                  <LabelGroup title="Email">{iboA?.email ?? "???"}</LabelGroup>
                </Column>
                <Column width="23.3333%">
                  <LabelGroup title="Phone">{iboA?.phone ?? "???"}</LabelGroup>
                </Column>
              </Row>
              <Row>
                <Column>
                  <LabelGroup title="Username">
                    <UsernameEdit />
                  </LabelGroup>
                </Column>
              </Row>
            </Section>
          </Card>

          <Card>
            <Membership
              iboNumber={iboNumber}
              membership={membership}
              hasFreeTrial={hasFreeTrial}
              hasDigitalDelivery={hasDigitalDelivery}
              hasDreamStream={hasDreamStream}
              hasPendingDeliveryDowngrade={hasPendingDeliveryDowngrade}
              hasPendingDreamStreamDowngrade={hasPendingDreamStreamDowngrade}
              hasPendingMembershipTransactions={
                hasPendingMembershipTransactions
              }
            />
          </Card>

          <Tabs
            iboNumber={iboNumber}
            selectedTab={query.get("tab")}
            onTabChange={(tabName, options) => {
              query.set({ tab: tabName }, options);
            }}
            membership={membership}
          />
        </>
      )}

      <Card>
        <Section>
          <header>
            <h3>Line Of Sponsorship</h3>
          </header>
          <Row>
            <Column width="30%">
              <LabelGroup title="LOS Pin Level">
                <PinLabel pinLevel={pinLevel} />
              </LabelGroup>

              {pinLevel > 1 && (
                <LabelGroup title="Award Pin Level">
                  {los.awardPinLevel ? (
                    los.awardPinLevel
                  ) : (
                    <span className={styles.faded}>None</span>
                  )}
                </LabelGroup>
              )}
            </Column>
            <Column width="30%">
              <LabelGroup title="Sponsor">
                {los.sponsor.hasAccount ? (
                  <Link to={getMemberUrl(los.sponsor.ibo)}>
                    {los.sponsor.ibo} {los.sponsor.name}
                  </Link>
                ) : (
                  <span>
                    {los.sponsor.ibo} {los.sponsor.name}
                  </span>
                )}
              </LabelGroup>
              <LabelGroup title="Platinum">
                {los.platinum.hasAccount ? (
                  <Link to={getMemberUrl(los.platinum.ibo)}>
                    {los.platinum.ibo} {los.platinum.name}
                  </Link>
                ) : (
                  <span>
                    {los.platinum.ibo} {los.platinum.name}
                  </span>
                )}
              </LabelGroup>
            </Column>
            <Column width="40%">
              <LabelGroup title="Emerald">
                {los.emerald.hasAccount ? (
                  <Link to={getMemberUrl(los.emerald.ibo)}>
                    {los.emerald.ibo} {los.emerald.name}
                  </Link>
                ) : (
                  <span>
                    {los.emerald.ibo} {los.emerald.name}
                  </span>
                )}
              </LabelGroup>
              <LabelGroup title="Diamond">
                {los.diamond.hasAccount ? (
                  <Link to={getMemberUrl(los.diamond.ibo)}>
                    {los.diamond.ibo} {los.diamond.name}
                  </Link>
                ) : (
                  <span>
                    {los.diamond.ibo} {los.diamond.name}
                  </span>
                )}
              </LabelGroup>
            </Column>
          </Row>

          <div className={styles.losView}>
            <LOS iboInfo={iboInfo} iboNumber={iboNumber} />
          </div>
        </Section>
      </Card>
    </div>
  );
}
